'use client';
import {
  Box,
  ButtonBase,
  ButtonProps,
  darken,
  getContrastRatio,
  lighten,
  useTheme,
  CircularProgress,
  Typography,
} from '@mui/material';
import Optional from '@jd/ui/components/Optional';

const buttonSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  textTransform: 'uppercase',
  px: 3,
  py: 1.5,
  borderRadius: 1,
  backgroundColor: 'primary.main',
  color: 'primary.contrastText',
};

const AnimatedShimmerButton = ({ children, loading, ...rest }: ButtonProps) => {
  const theme = useTheme();
  const { loadingPosition, ...baseButtonProps } = rest;

  const contrastToDark = getContrastRatio(theme.palette.primary.main, '#000');
  const bgFn = contrastToDark > 2 ? darken : lighten;

  return (
    <ButtonBase
      {...baseButtonProps}
      disabled={loading || baseButtonProps.disabled}
      sx={{
        ...buttonSx,
        position: 'relative',
        overflow: 'hidden',
        transition:
          'transform 0.2s ease, box-shadow 0.2s ease, background-color 0.2s ease',
        ':hover, :focus': {
          textDecoration: 'none !important',
          backgroundColor: bgFn(theme.palette.primary.main, 0.1),
          transform: loading ? 'none' : 'translateY(-2px)',
          boxShadow: loading ? 'none' : theme.shadows[4],
        },
        // The Shimmer Beam
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: '-100%',
          width: '50%',
          height: '100%',
          background:
            'linear-gradient(to right, transparent, rgba(255, 255, 255, 0.3), transparent)',
          transform: 'skewX(-25deg)',
          transition: 'none',
        },
        ':hover::after': {
          left: loading ? '-100%' : '200%',
          transition: 'left 0.7s ease-in-out',
        },
        ...baseButtonProps.sx,
      }}
    >
      <Typography
        variant={'button'}
        sx={{
          opacity: loading ? 0 : 1,
          transition: 'opacity 0.2s ease',
          fontSize: 15,
          lineHeight: 1.75,
          whiteSpace: 'nowrap',
          zIndex: 1,
        }}
      >
        {children}
      </Typography>

      <Optional condition={!!loading}>
        <Box
          sx={{
            position: 'absolute',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2,
          }}
        >
          <CircularProgress size={24} sx={{ color: 'primary.contrastText' }} />
        </Box>
      </Optional>
    </ButtonBase>
  );
};

export default AnimatedShimmerButton;
